import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { EventConfig, Locale } from '@/types'

interface AppState {
  locale: Locale
  setLocale: (locale: Locale) => void
  darkMode: boolean
  setDarkMode: (v: boolean) => void
  sidebarOpen: boolean
  setSidebarOpen: (v: boolean) => void
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      locale: 'en',
      setLocale: (locale) => set({ locale }),
      darkMode: true,
      setDarkMode: (darkMode) => set({ darkMode }),
      sidebarOpen: true,
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
    }),
    { name: 'posebox-app' }
  )
)

interface EventState {
  events: EventConfig[]
  activeEventId: string | null
  setEvents: (events: EventConfig[]) => void
  addEvent: (event: EventConfig) => void
  updateEvent: (id: string, updates: Partial<EventConfig>) => void
  deleteEvent: (id: string) => void
  renameEventId: (oldId: string, newId: string) => void
  setActiveEventId: (id: string | null) => void
  loadEvents: () => Promise<void>
  saveEvent: (event: EventConfig) => Promise<void>
}

export const useEventStore = create<EventState>((set, get) => ({
  events: [],
  activeEventId: null,
  setEvents: (events) => set({ events }),
  addEvent: (event) => set((s) => ({ events: [event, ...s.events] })),
  updateEvent: (id, updates) =>
    set((s) => ({
      events: s.events.map((e) => (e.id === id ? { ...e, ...updates, updatedAt: new Date().toISOString() } : e)),
    })),
  deleteEvent: (id) => set((s) => ({ events: s.events.filter((e) => e.id !== id) })),
  renameEventId: (oldId, newId) =>
    set((s) => ({
      events: s.events.map((e) => (e.id === oldId ? { ...e, id: newId } : e)),
    })),
  setActiveEventId: (id) => set({ activeEventId: id }),
  loadEvents: async () => {
    try {
      const res = await fetch('/api/events')
      if (!res.ok) throw new Error('Failed to load events')
      const data = await res.json()
      const events = await Promise.all(
        data.map(async (ev: { id: string; config: EventConfig }) => {
          const detail = await fetch(`/api/events/${ev.id}`).then(r => r.json())
          return detail.config as EventConfig
        })
      )
      set({ events })
    } catch {
      set({ events: [] })
    }
  },
  saveEvent: async (event) => {
    const exists = get().events.find((e) => e.id === event.id)
    const method = exists ? 'PUT' : 'POST'
    const url = exists ? `/api/events/${event.id}` : '/api/events'
    await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: event.id, name: event.name, config: event }),
    })
    if (exists) {
      set((s) => ({
        events: s.events.map((e) => (e.id === event.id ? event : e)),
      }))
    } else {
      set((s) => ({ events: [event, ...s.events] }))
    }
  },
}))

interface User {
  id: string
  email: string
  name: string
}

interface AuthState {
  user: User | null
  initialized: boolean
  setUser: (user: User | null) => void
  checkAuth: () => Promise<void>
  logout: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  initialized: false,
  setUser: (user) => set({ user, initialized: true }),
  checkAuth: async () => {
    try {
      const res = await fetch('/api/auth/me')
      if (res.ok) {
        const data = await res.json()
        set({ user: data.user, initialized: true })
      } else {
        set({ user: null, initialized: true })
      }
    } catch {
      set({ user: null, initialized: true })
    }
  },
  logout: async () => {
    await fetch('/api/auth/logout', { method: 'POST' })
    set({ user: null })
  }
}))
